import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ChangePasswordCard from "./ChangePasswordCard";
import EmailSettingsCard from "./EmailSettingsCard";
import SiteIdentityCard from "./SiteIdentityCard";
import BrandAssetsCard from "./BrandAssetsCard";

export default function SettingsManager() {
  const [text, setText] = useState("");
  const [hasLogo, setHasLogo] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const [logoFile, setLogoFile] = useState(null);
  const [savingText, setSavingText] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [logoBlobUrl, setLogoBlobUrl] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setText(data.watermark_text || "");
      setHasLogo(!!data.has_logo);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount; load is stable per render
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
  useEffect(() => { load(); }, []);

  // SEC-004: fetch the logo as an authenticated blob rather than exposing the
  // JWT via a query string. The object URL is revoked on unmount / re-fetch.
  useEffect(() => {
    let currentUrl = null;
    (async () => {
      if (!hasLogo) {
        setLogoBlobUrl(null);
        return;
      }
      try {
        const resp = await api.get("/admin/settings/logo", { responseType: "blob" });
        currentUrl = URL.createObjectURL(resp.data);
        setLogoBlobUrl(currentUrl);
      } catch (e) {
        setLogoBlobUrl(null);
      }
    })();
    return () => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [hasLogo, logoVersion]);

  const saveText = async (e) => {
    e.preventDefault();
    setSavingText(true);
    try {
      await api.put("/admin/settings", { watermark_text: text });
      toast.success("Watermark text saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingText(false);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) { toast.error("Choose a logo file first."); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", logoFile);
      await api.post("/admin/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      setLogoFile(null);
      setHasLogo(true);
      setLogoVersion((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the logo? New uploads will only have the text watermark.")) return;
    try {
      await api.delete("/admin/settings/logo");
      setHasLogo(false);
      setLogoVersion((v) => v + 1);
      toast.success("Logo removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-12" data-testid="settings-manager">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <form onSubmit={saveText} className="space-y-5">
          <h3 className="font-serif text-2xl">Watermark text</h3>
          <p className="text-sm text-white/50">
            Tiled diagonally at low opacity across every new preview thumbnail. The original
            full-resolution file is never watermarked — buyers receive the clean image.
          </p>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Text</label>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={60}
              placeholder="e.g. SIDELINE PHOTO"
              className="input-minimal"
              data-testid="watermark-text-input"
            />
          </div>
          <button
            type="submit"
            disabled={savingText}
            className="bg-white text-black px-8 py-3 text-xs uppercase tracking-[0.3em] hover:bg-white/90 disabled:opacity-50"
            data-testid="watermark-text-save"
          >
            {savingText ? "Saving…" : "Save text"}
          </button>
          <p className="text-xs text-white/40">Existing thumbnails keep their original watermark — only newly uploaded photos use this text.</p>
        </form>

        <div className="space-y-5">
          <h3 className="font-serif text-2xl">Logo overlay</h3>
          <p className="text-sm text-white/50">
            Optional. A PNG with transparency works best. Placed in the bottom-right corner of every new preview.
          </p>

          {hasLogo && (
            <div className="border border-white/10 p-4 bg-[#0a0a0a]">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">Current logo</p>
              <div className="bg-[#222] p-4 inline-block">
                <img
                  key={logoVersion}
                  src={logoBlobUrl || ""}
                  alt="Watermark logo"
                  className="max-h-32"
                  data-testid="current-logo"
                />
              </div>
              <div className="mt-3">
                <button
                  onClick={removeLogo}
                  className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300"
                  data-testid="logo-remove"
                >
                  Remove logo
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-2">
              {hasLogo ? "Replace logo" : "Upload logo"}
            </label>
            <input
              type="file"
              accept="image/png,image/webp,image/jpeg"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              className="text-sm text-white/70 file:mr-3 file:px-4 file:py-2 file:border file:border-white/20 file:bg-transparent file:text-white file:text-xs file:uppercase file:tracking-[0.2em] hover:file:border-white/60"
              data-testid="logo-file-input"
            />
            {logoFile && (
              <p className="text-xs text-white/50 mt-2">Selected: {logoFile.name} ({(logoFile.size / 1024).toFixed(1)} KB)</p>
            )}
          </div>

          <button
            onClick={uploadLogo}
            disabled={!logoFile || uploadingLogo}
            className="bg-white text-black px-8 py-3 text-xs uppercase tracking-[0.3em] hover:bg-white/90 disabled:opacity-50"
            data-testid="logo-upload-button"
          >
            {uploadingLogo ? "Uploading…" : hasLogo ? "Replace logo" : "Upload logo"}
          </button>
        </div>
      </div>

      <RetentionSettings />

      <SiteIdentityCard />

      <BrandAssetsCard />

      <EmailSettingsCard />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
        <div className="space-y-3">
          <h3 className="font-serif text-2xl">Account security</h3>
          <p className="text-sm text-white/50 font-condensed">
            Rotate your admin password. Restarts of the backend no longer overwrite this — you own it now.
          </p>
        </div>
        <ChangePasswordCard />
      </div>

      <LegacyMigration />

      <FtpSettings />
    </div>
  );
}

function RetentionSettings() {
  const [cfg, setCfg] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [lastSweep, setLastSweep] = useState(null);
  const [digestSending, setDigestSending] = useState(false);
  const [lastDigest, setLastDigest] = useState(null);

  const load = async () => {
    try {
      const [c, u] = await Promise.all([
        api.get("/admin/retention/config"),
        api.get("/admin/storage/usage"),
      ]);
      setCfg(c.data);
      setUsage(u.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/retention/config", cfg);
      setCfg(data);
      toast.success("Retention settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const runSweep = async (dry) => {
    setSweeping(true);
    try {
      const { data } = await api.post(`/admin/retention/sweep?dry_run=${dry ? "true" : "false"}`);
      setLastSweep(data);
      if (dry) {
        toast.info(`Dry run · ${data.expired_now} to expire, ${data.deleted_now} to delete`);
      } else {
        toast.success(`Sweep complete · ${data.expired_now} expired, ${data.deleted_now} deleted, ${data.delivery_expired_now} delivery copies purged`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSweeping(false);
    }
  };

  const runDigest = async (dry) => {
    setDigestSending(true);
    try {
      const { data } = await api.post(`/admin/retention/digest?dry_run=${dry ? "true" : "false"}`);
      setLastDigest(data);
      if (data.skipped) {
        toast.info("Digest disabled in settings");
      } else if (data.resend_configured) {
        toast.success(`${data.emails_sent} email${data.emails_sent===1?"":"s"} sent to ${data.photographers_notified} photographer${data.photographers_notified===1?"":"s"}`);
      } else {
        toast.warning(`Would notify ${data.photographers_notified} photographer(s), but Resend API key is missing`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDigestSending(false);
    }
  };

  if (!cfg) return <div className="border-t border-white/10 pt-10"><p className="text-white/50 text-sm">Loading retention config…</p></div>;

  const gbUsed = usage?.live_gb ?? 0;
  const freeTierUsedPct = Math.min(100, (gbUsed / 10) * 100);

  return (
    <div className="border-t border-white/10 pt-10 space-y-8" data-testid="retention-settings">
      <div>
        <h3 className="font-serif text-2xl">Photo retention</h3>
        <p className="text-sm text-white/50 mt-1 max-w-2xl">
          Photos stay live for a fixed window after upload. When they expire they&apos;re hidden from the storefront; after a grace period the original file is permanently removed from R2 storage. Purchased photos are copied to a delivery vault so buyers still get their download for the delivery window.
        </p>
      </div>

      <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <NumField label="Free retention window (days)" value={cfg.retention_days} onChange={(v) => setCfg({ ...cfg, retention_days: v })} testId="retention-days" />
        <NumField label="Grace period after expiry (days)" value={cfg.grace_period_days} onChange={(v) => setCfg({ ...cfg, grace_period_days: v })} testId="grace-days" />
        <NumField label="Buyer download availability (days)" value={cfg.delivery_days} onChange={(v) => setCfg({ ...cfg, delivery_days: v })} testId="delivery-days" />
        <NumField label='"Expiring soon" warning (days)' value={cfg.expiring_soon_days} onChange={(v) => setCfg({ ...cfg, expiring_soon_days: v })} testId="expiring-soon-days" />

        {/* --- Scheduler --- */}
        <div className="md:col-span-2 border-t border-white/10 pt-6 mt-2">
          <h4 className="font-serif text-xl">Automatic schedule</h4>
          <p className="text-sm text-white/50 font-condensed mt-1">
            The background scheduler runs the sweep + expiry-warning digest on this cadence. Times are UTC.
          </p>
        </div>
        <label className="flex items-center gap-3 text-sm text-white/70 font-condensed md:col-span-2">
          <input type="checkbox" checked={!!cfg.sweep_enabled} onChange={(e) => setCfg({ ...cfg, sweep_enabled: e.target.checked })} className="accent-[var(--pm-orange)]" data-testid="sweep-enabled" />
          Enable automatic sweep + digest
        </label>
        <NumField label="Frequency (hours between runs)" value={cfg.sweep_frequency_hours} onChange={(v) => setCfg({ ...cfg, sweep_frequency_hours: v })} testId="sweep-freq" min={1} max={168} />
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Anchor time (UTC · HH:MM, used when ≥ 24h)</label>
          <input
            type="text"
            value={cfg.sweep_time_utc ?? "03:00"}
            onChange={(e) => setCfg({ ...cfg, sweep_time_utc: e.target.value })}
            className="input-minimal"
            placeholder="03:00"
            data-testid="sweep-time"
          />
        </div>
        <label className="flex items-center gap-3 text-sm text-white/70 font-condensed md:col-span-2">
          <input type="checkbox" checked={!!cfg.email_digest_enabled} onChange={(e) => setCfg({ ...cfg, email_digest_enabled: e.target.checked })} className="accent-[var(--pm-orange)]" data-testid="email-digest-enabled" />
          Send warning emails to photographers about expiring photos
          {!cfg.resend_configured && <span className="text-[10px] uppercase tracking-[0.2em] pm-orange">Resend API key missing — emails won&apos;t send</span>}
        </label>

        {/* --- Extension pricing --- */}
        <div className="md:col-span-2 border-t border-white/10 pt-6 mt-2">
          <h4 className="font-serif text-xl">Extension pricing</h4>
          <p className="text-sm text-white/50 font-condensed mt-1">
            Set the fee photographers pay to extend a photo&apos;s retention past its free window. Set to 0 to keep extensions free.
          </p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Price per photo per 30 days</label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              step="0.01"
              value={cfg.extension_price_per_30d ?? 0}
              onChange={(e) => setCfg({ ...cfg, extension_price_per_30d: Number(e.target.value) || 0 })}
              className="input-minimal flex-1"
              data-testid="ext-price"
            />
            <select
              value={cfg.extension_currency ?? "gbp"}
              onChange={(e) => setCfg({ ...cfg, extension_currency: e.target.value })}
              className="input-minimal bg-black !w-24 shrink-0"
              data-testid="ext-currency"
            >
              <option value="gbp">GBP</option>
              <option value="usd">USD</option>
              <option value="eur">EUR</option>
            </select>
          </div>
          <p className="text-xs text-white/40 mt-1 font-condensed">
            Currently: {(cfg.extension_price_per_30d ?? 0) === 0 ? "Free" : `${cfg.extension_currency?.toUpperCase()} ${(cfg.extension_price_per_30d ?? 0).toFixed(2)} per photo per month`}
          </p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">90-day discount (%)</label>
          <input
            type="number"
            min="0"
            max="90"
            value={Math.round((cfg.extension_90d_discount ?? 0.15) * 100)}
            onChange={(e) => setCfg({ ...cfg, extension_90d_discount: (Number(e.target.value) || 0) / 100 })}
            className="input-minimal"
            data-testid="ext-90-discount"
          />
          <p className="text-xs text-white/40 mt-1 font-condensed">Off the base 90-day price</p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">365-day discount (%)</label>
          <input
            type="number"
            min="0"
            max="90"
            value={Math.round((cfg.extension_365d_discount ?? 0.40) * 100)}
            onChange={(e) => setCfg({ ...cfg, extension_365d_discount: (Number(e.target.value) || 0) / 100 })}
            className="input-minimal"
            data-testid="ext-365-discount"
          />
          <p className="text-xs text-white/40 mt-1 font-condensed">Off the base annual price</p>
        </div>

        <div className="md:col-span-2">
          <button
            type="submit"
            disabled={busy}
            className="bg-white text-black px-8 py-3 text-xs uppercase tracking-[0.3em] hover:bg-white/90 disabled:opacity-50"
            data-testid="retention-save"
          >
            {busy ? "Saving…" : "Save all retention settings"}
          </button>
        </div>
      </form>

      {usage && (
        <div className="border border-white/10 p-5 bg-[#0a0a0a] space-y-4" data-testid="storage-usage">
          <div className="flex items-baseline justify-between">
            <h4 className="font-serif text-xl">Storage</h4>
            <p className="text-xs uppercase tracking-[0.2em] text-white/60">
              R2 est. bill: <span className="pm-orange font-semibold">${usage.est_r2_monthly_cost_usd.toFixed(2)}/mo</span>
            </p>
          </div>
          <div>
            <div className="flex items-baseline justify-between text-xs text-white/60 mb-1 font-condensed">
              <span>{gbUsed.toFixed(2)} GB used across {usage.active.count + usage.expired.count} live photos</span>
              <span>10 GB free tier</span>
            </div>
            <div className="h-2 bg-white/10 overflow-hidden">
              <div
                className={`h-full ${freeTierUsedPct > 80 ? "bg-red-500" : freeTierUsedPct > 50 ? "bg-yellow-500" : "bg-[var(--pm-orange)]"}`}
                style={{ width: `${freeTierUsedPct}%` }}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-xs font-condensed">
            <div><span className="text-white/40 uppercase tracking-[0.2em]">Active</span><br /><span className="font-display text-2xl uppercase">{usage.active.count}</span></div>
            <div><span className="text-white/40 uppercase tracking-[0.2em]">Expired · grace</span><br /><span className="font-display text-2xl uppercase">{usage.expired.count}</span></div>
            <div><span className="text-white/40 uppercase tracking-[0.2em]">Removed</span><br /><span className="font-display text-2xl uppercase">{usage.deleted.count}</span></div>
          </div>
          {usage.expired.count > 0 && (
            <button
              onClick={async () => {
                if (!window.confirm(`Permanently delete ALL ${usage.expired.count} expired photos platform-wide? Files are removed from storage and cannot be recovered.`)) return;
                try {
                  const { data } = await api.post("/photographer/expired/delete-all");
                  toast.success(`Deleted ${data.deleted} expired photo${data.deleted === 1 ? "" : "s"}${data.failed ? ` · ${data.failed} failed` : ""}`);
                  await load();
                } catch (err) {
                  toast.error(formatApiError(err));
                }
              }}
              className="border border-red-500 text-red-400 hover:bg-red-500 hover:text-black px-5 py-2 text-xs uppercase tracking-[0.3em] font-semibold"
              data-testid="admin-delete-all-expired"
            >
              Delete all expired · {usage.expired.count}
            </button>
          )}
        </div>
      )}

      <div className="border border-white/10 p-5 bg-[#0a0a0a] space-y-3" data-testid="sweep-panel">
        <div className="flex items-baseline justify-between">
          <h4 className="font-serif text-xl">Run manually</h4>
          {cfg.last_sweep_at && (
            <p className="text-xs text-white/40 font-condensed">
              Last auto-run: {new Date(cfg.last_sweep_at).toLocaleString()}
            </p>
          )}
        </div>
        <p className="text-sm text-white/50 font-condensed">
          Fires the retention sweep and expiry-warning digest immediately, ignoring the schedule.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => runSweep(true)}
            disabled={sweeping}
            className="border border-white/20 hover:border-white/60 px-6 py-3 text-xs uppercase tracking-[0.3em] disabled:opacity-50"
            data-testid="sweep-dry"
          >
            {sweeping ? "…" : "Sweep · dry run"}
          </button>
          <button
            onClick={() => runSweep(false)}
            disabled={sweeping}
            className="bg-[var(--pm-orange)] text-black px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[var(--pm-orange-bright)] disabled:opacity-50"
            data-testid="sweep-run"
          >
            {sweeping ? "Running…" : "Run sweep now"}
          </button>
          <button
            onClick={() => runDigest(false)}
            disabled={digestSending}
            className="border border-[var(--pm-orange)] pm-orange px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[var(--pm-orange)] hover:text-black disabled:opacity-50"
            data-testid="digest-run"
          >
            {digestSending ? "Sending…" : "Send expiry emails now"}
          </button>
        </div>
        {lastSweep && (
          <div className="text-xs text-white/60 font-condensed pt-2 border-t border-white/10">
            Last {lastSweep.dry_run ? "dry run" : "sweep"} · expired {lastSweep.expired_now} · deleted {lastSweep.deleted_now} · failed {lastSweep.delete_failed_now || 0} · delivery purged {lastSweep.delivery_expired_now}
          </div>
        )}
        {lastDigest && (
          <div className="text-xs text-white/60 font-condensed pt-2 border-t border-white/10">
            Last digest{lastDigest.dry_run ? " (dry run)" : ""} · {lastDigest.photographers_notified || 0} photographer{(lastDigest.photographers_notified||0)===1?"":"s"} notified · {lastDigest.emails_sent||0} emails sent · {lastDigest.total_photos||0} photos flagged
            {!lastDigest.resend_configured && !lastDigest.dry_run && <span className="pm-orange"> · Resend key missing</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, testId, min = 1, max = 3650 }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">{label}</label>
      <input
        type="number"
        min={min}
        max={max}
        value={value ?? ""}
        onChange={(e) => onChange(Number(e.target.value) || min)}
        className="input-minimal"
        data-testid={testId}
      />
    </div>
  );
}

function LegacyMigration() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [running, setRunning] = useState(false);
  const [stopFlag, setStopFlag] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/storage/migrate-legacy/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
  useEffect(() => { load(); }, []);

  const runBatch = async (dry) => {
    try {
      const { data } = await api.post(`/admin/storage/migrate-legacy?limit=10&dry_run=${dry ? "true" : "false"}`);
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  };

  const runOne = async () => {
    setRunning(true);
    setLog([]);
    setStopFlag(false);
    const data = await runBatch(true);
    if (data) {
      setLog(data.results);
      toast.info(`Dry run · ${data.batch_size} photos would migrate · ${data.remaining} pending total`);
    }
    setRunning(false);
  };

  const runAll = async () => {
    setRunning(true);
    setLog([]);
    setStopFlag(false);
    let totalMoved = 0;
    let totalFailed = 0;
    let totalBytes = 0;
    // Loop until remaining hits 0 (or stopped)
    while (true) {
      if (stopFlag) break;
      const data = await runBatch(false);
      if (!data) break;
      setLog((prev) => [...data.results, ...prev].slice(0, 200));
      totalMoved += data.migrated_now;
      totalFailed += data.failed_now;
      totalBytes += data.bytes_moved;
      await load();
      if (data.remaining === 0 || data.batch_size === 0) break;
      // brief pause between batches so UI stays responsive
      await new Promise((r) => setTimeout(r, 250));
    }
    setRunning(false);
    toast.success(`Migration finished · ${totalMoved} moved, ${totalFailed} failed, ${(totalBytes/(1024*1024)).toFixed(1)} MB`);
  };

  if (!status) {
    return (
      <div className="border-t border-white/10 pt-10">
        <p className="text-white/50 text-sm">Loading migration status…</p>
      </div>
    );
  }

  if (!status.r2_enabled) {
    return (
      <div className="border-t border-white/10 pt-10" data-testid="legacy-migration">
        <h3 className="font-serif text-2xl">Legacy storage migration</h3>
        <p className="text-sm text-red-400 mt-2">R2 is not configured. Set R2_* env vars in backend/.env.</p>
      </div>
    );
  }

  const donePct = status.total > 0 ? Math.round((status.migrated / status.total) * 100) : 100;

  return (
    <div className="border-t border-white/10 pt-10 space-y-6" data-testid="legacy-migration">
      <div>
        <h3 className="font-serif text-2xl">Legacy storage migration</h3>
        <p className="text-sm text-white/50 mt-1 max-w-2xl">
          One-shot backfill for photos still stored on the retired Emergent backend. This copies the original + thumb into your R2 bucket so the retention engine can delete them properly. Safe to re-run — already-migrated photos are skipped.
        </p>
      </div>

      <div className="border border-white/10 p-5 bg-[#0a0a0a] space-y-4">
        <div className="grid grid-cols-3 gap-4 text-xs font-condensed">
          <div>
            <span className="text-white/40 uppercase tracking-[0.2em]">Total</span><br />
            <span className="font-display text-2xl uppercase" data-testid="legacy-total">{status.total}</span>
          </div>
          <div>
            <span className="text-white/40 uppercase tracking-[0.2em]">Migrated · R2</span><br />
            <span className="font-display text-2xl uppercase text-green-400" data-testid="legacy-migrated">{status.migrated}</span>
          </div>
          <div>
            <span className="text-white/40 uppercase tracking-[0.2em]">Pending · Emergent</span><br />
            <span className="font-display text-2xl uppercase pm-orange" data-testid="legacy-pending">{status.pending}</span>
          </div>
        </div>
        <div>
          <div className="flex items-baseline justify-between text-xs text-white/60 mb-1 font-condensed">
            <span>{donePct}% migrated</span>
            <span>{status.pending_mb?.toLocaleString?.() || 0} MB left to move</span>
          </div>
          <div className="h-2 bg-white/10 overflow-hidden">
            <div className="h-full bg-green-500" style={{ width: `${donePct}%` }} />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            onClick={runOne}
            disabled={running || status.pending === 0}
            className="border border-white/20 hover:border-white/60 px-6 py-3 text-xs uppercase tracking-[0.3em] disabled:opacity-50"
            data-testid="legacy-dry-run"
          >
            Dry run · next 10
          </button>
          <button
            onClick={runAll}
            disabled={running || status.pending === 0}
            className="bg-[var(--pm-orange)] text-black px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[var(--pm-orange-bright)] disabled:opacity-50"
            data-testid="legacy-run-all"
          >
            {running ? "Migrating…" : `Migrate all (${status.pending})`}
          </button>
          {running && (
            <button
              onClick={() => setStopFlag(true)}
              className="border border-red-500 text-red-400 hover:bg-red-500 hover:text-black px-6 py-3 text-xs uppercase tracking-[0.3em]"
              data-testid="legacy-stop"
            >
              Stop after batch
            </button>
          )}
          <button
            onClick={load}
            disabled={running}
            className="border border-white/10 hover:border-white/40 px-4 py-3 text-xs uppercase tracking-[0.3em] disabled:opacity-50"
            data-testid="legacy-refresh"
          >
            Refresh
          </button>
        </div>

        {status.pending === 0 && (
          <div className="text-xs pm-orange font-condensed pt-2 border-t border-white/10">
            All photos are already on R2. Nothing to migrate.
          </div>
        )}
      </div>

      {log.length > 0 && (
        <div className="border border-white/10 p-4 bg-[#050505] max-h-64 overflow-y-auto text-xs font-mono space-y-1" data-testid="legacy-log">
          {log.map((r, i) => (
            <div key={`${r.id}-${i}`} className="flex gap-3 items-baseline">
              <span className={
                r.status === "migrated" ? "text-green-400" :
                r.status === "dry_run" ? "text-white/50" :
                r.status === "error" ? "text-red-400" : "text-white/40"
              }>
                {r.status === "migrated" ? "✓" : r.status === "error" ? "✗" : "·"}
              </span>
              <span className="text-white/60 truncate">{r.filename || r.id}</span>
              {r.error && <span className="text-red-400/80 truncate">{r.error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FtpSettings() {
  const [host, setHost] = useState("");
  const [port, setPort] = useState(21);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [defaultPath, setDefaultPath] = useState("/");
  const [usePassive, setUsePassive] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/ftp/settings");
      setConfigured(!!data.configured);
      setHost(data.host || "");
      setPort(data.port || 21);
      setUsername(data.username || "");
      setDefaultPath(data.default_path || "/");
      setUsePassive(data.use_passive !== false);
    } catch (e) {
      console.warn("FTP settings load failed (endpoint may be unconfigured):", e?.response?.status || e?.message);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/ftp/settings", {
        host, port: Number(port) || 21, username,
        password: password || undefined,
        default_path: defaultPath, use_passive: usePassive,
      });
      toast.success("FTP settings saved");
      setPassword("");
      setConfigured(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/ftp/test");
      toast.success(`Connected · ${data.cwd ? `cwd ${data.cwd}` : "ok"}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5 border-t border-white/10 pt-10" data-testid="ftp-settings">
      <div className="flex items-end justify-between">
        <div>
          <h3 className="font-serif text-2xl">FTP photo source</h3>
          <p className="text-sm text-white/50 mt-1">
            Pull photos straight from an FTP server during a race day. Password is encrypted at rest. Server must be reachable on the public internet.
          </p>
        </div>
        {configured && (
          <span className="text-xs uppercase tracking-[0.3em] text-green-400">Configured</span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Host</label>
          <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="ftp.example.com" required className="input-minimal" data-testid="ftp-host" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Port</label>
          <input type="number" value={port} onChange={(e) => setPort(e.target.value)} className="input-minimal" data-testid="ftp-port" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Username</label>
          <input value={username} onChange={(e) => setUsername(e.target.value)} required className="input-minimal" data-testid="ftp-username" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">
            Password {configured && <span className="text-white/30 normal-case tracking-normal">(leave blank to keep)</span>}
          </label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-minimal" data-testid="ftp-password" />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Default folder path</label>
        <input value={defaultPath} onChange={(e) => setDefaultPath(e.target.value)} placeholder="/" className="input-minimal" data-testid="ftp-default-path" />
      </div>

      <label className="flex items-center gap-3 text-sm text-white/60">
        <input type="checkbox" checked={usePassive} onChange={(e) => setUsePassive(e.target.checked)} className="accent-white" data-testid="ftp-passive" />
        Use passive mode (recommended)
      </label>

      <div className="flex gap-3">
        <button type="submit" disabled={busy} className="bg-white text-black px-8 py-3 text-xs uppercase tracking-[0.3em] hover:bg-white/90 disabled:opacity-50" data-testid="ftp-save">
          {busy ? "Saving…" : "Save FTP settings"}
        </button>
        <button type="button" onClick={testConnection} disabled={testing || !configured} className="border border-white/20 px-8 py-3 text-xs uppercase tracking-[0.3em] hover:border-white/60 disabled:opacity-50" data-testid="ftp-test">
          {testing ? "Testing…" : "Test connection"}
        </button>
      </div>
    </form>
  );
}
